"use client";

import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShoppingBag, Loader2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import CartItem from "./CartItem";
import { PALETTE } from "../lib/colorPallete";
import Link from "next/link";
import { useAuth } from "../context/AuthContext";
import { calculatePrice } from "../lib/pricingService";
import { CartDrawerProps } from "../types/types";

const CartDrawer: React.FC<CartDrawerProps> = ({ onClose, onEditItem }) => {
  const { cart, isLoading, error } = useCart();
  const { isWholesaleUser } = useAuth();
  const subtotal = useMemo(() => {
    if (!cart?.items) return 0;
    console.log("Cart data for subtotal calculation:", cart);

    return cart.items.reduce((total, item) => {
      if (!item.product) return total;

      const priceBreakdown = calculatePrice(
        item.product,
        item.selectedOptions,
        isWholesaleUser,
      );
      return total + priceBreakdown.total * item.quantity;
    }, 0);
  }, [cart, isWholesaleUser]);

  return (
    <>
      {}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, pointerEvents: "none" }}
        transition={{ duration: 0.4 }}
        className="fixed inset-0 bg-black/60 z-50"
        onClick={onClose}
      />

      {}
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "tween", ease: "easeInOut", duration: 0.4 }}
        className="fixed top-0 right-0 h-full w-full max-w-md z-50 flex flex-col shadow-2xl"
        style={{ backgroundColor: PALETTE.cream }}
      >
        {}
        <div
          className="flex items-center justify-between p-5 shrink-0"
          style={{ borderBottom: `1px solid ${PALETTE.midGreen}40` }}
        >
          <h2
            className="text-2xl font-serif font-bold flex items-center gap-2"
            style={{ color: PALETTE.darkGreen }}
          >
            <ShoppingBag size={22} /> Your Cart
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full transition-colors"
            style={{
              color: PALETTE.darkGreen,
              backgroundColor: `${PALETTE.darkGreen}10`,
            }}
          >
            <X size={20} />
          </button>
        </div>

        {}
        <div className="flex-grow overflow-y-auto p-5">
          {isLoading && !cart && (
            <div className="flex justify-center items-center h-full">
              <Loader2
                className="animate-spin"
                size={32}
                style={{ color: PALETTE.darkGreen }}
              />
            </div>
          )}
          {error && <p className="text-center text-red-500">{error}</p>}

          {cart && cart.items.length > 0 ? (
            <div
              className="divide-y"
              style={{ borderColor: `${PALETTE.midGreen}40` }}
            >
              <AnimatePresence>
                {cart.items.map((item) => (
                  <CartItem key={item._id} item={item} onEdit={onEditItem} />
                ))}
              </AnimatePresence>
            </div>
          ) : (
            !isLoading && (
              <div
                className="text-center h-full flex flex-col items-center justify-center"
                style={{ color: `${PALETTE.darkGreen}99` }}
              >
                <ShoppingBag size={48} className="mx-auto opacity-50" />
                <p
                  className="mt-4 text-lg font-semibold"
                  style={{ color: PALETTE.darkGreen }}
                >
                  Your cart is empty
                </p>
                <p className="text-sm max-w-xs mx-auto mt-1">
                  Looks like you haven&apos;t added anything yet. Explore our
                  products to find what you need.
                </p>
              </div>
            )
          )}
        </div>

        {}
        {cart && cart.items.length > 0 && (
          <div
            className="p-5 border-t space-y-4 shrink-0"
            style={{
              backgroundColor: PALETTE.cream,
              borderColor: `${PALETTE.midGreen}40`,
              boxShadow: "0 -5px 20px -5px rgba(0,0,0,0.05)",
            }}
          >
            <div
              className="flex justify-between font-semibold text-lg"
              style={{ color: PALETTE.darkGreen }}
            >
              <span>Subtotal</span>
              <span>
                {new Intl.NumberFormat("en-IN", {
                  style: "currency",
                  currency: "INR",
                  minimumFractionDigits: 0,
                }).format(subtotal)}
              </span>
            </div>
            <Link
              href={"/checkout"}
              className="w-full py-3 rounded-full font-semibold uppercase tracking-wider text-sm transition-all duration-300 hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{
                backgroundColor: PALETTE.darkerGreen,
                color: PALETTE.gold,
              }}
            >
              {isLoading ? (
                <Loader2 className="mx-auto animate-spin" />
              ) : (
                "Proceed to Checkout"
              )}
            </Link>
          </div>
        )}
      </motion.div>
    </>
  );
};

export default CartDrawer;
